import { useState } from "react";
import AppShell from "@/components/layout/AppShell";
import useAutoRefresh from "@/hooks/useAutoRefresh";
import StatusBadge from "@/components/common/StatusBadge";
import DataTable from "@/components/common/DataTable";
import AuthenticatedImage from "@/components/common/AuthenticatedImage";
import api from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Eye, User, ClockCounterClockwise, Image as ImageIcon } from "@phosphor-icons/react";

const ACCESS_LABEL = {
  supplier_receiving: "Supplier Barang Masuk",
  delivery_out: "Truk PT Keluar",
  pt_return: "Truk PT Kembali",
  tenant: "Penyewa (Tenant)",
  visitor: "Tamu",
};

export default function LogKendaraanPage() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);

  const load = () => {
    api.get("/gate/logs").then((r) => setRows(r.data)).catch(()=>{});
  };
  useAutoRefresh(load);

  const openDetail = (r) => {
    setDetail(r);
    api.get(`/gate/logs/${r.id}`).then((res) => setDetail(res.data)).catch(() => {});
  };

  const filtered = rows.filter((r) => {
    if (status !== "all" && r.status !== status) return false;
    if (q && !`${r.vehicle_no} ${r.company} ${r.driver_name} ${r.gate_in_by_name} ${r.gate_out_by_name}`
        .toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  const fmt = (iso) => iso ? new Date(iso).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" }) : "—";

  return (
    <AppShell title="Log Kendaraan" subtitle="Riwayat lengkap gerbang + jejak penanggung jawab per shift">
      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-5">
        <Input
          data-testid="log-search"
          placeholder="Cari plat, perusahaan, driver, petugas…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-sm h-10"
        />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="log-status-filter" className="w-48 h-10"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Status</SelectItem>
            <SelectItem value="in_area">In Area</SelectItem>
            <SelectItem value="exited">Keluar</SelectItem>
          </SelectContent>
        </Select>
        <span className="ml-auto text-xs font-mono text-slate-500">{filtered.length} baris</span>
      </div>

      <DataTable
        testid="log-table"
        rows={filtered}
        columns={[
          { key: "vehicle_no", label: "Kendaraan", render: (r) => <span className="font-mono font-bold">{r.vehicle_no}</span> },
          { key: "access_type", label: "Jenis Akses", render: (r) => (
            <span className="text-[12px] px-2 py-0.5 bg-slate-100 rounded-full text-slate-700">{ACCESS_LABEL[r.access_type] || r.access_type}</span>
          )},
          { key: "company", label: "Perusahaan" },
          { key: "driver_name", label: "Driver" },
          { key: "gate_in_at", label: "Masuk", render: (r) => (
            <div className="text-[11px] leading-tight">
              <div className="font-mono text-slate-700">{fmt(r.gate_in_at)}</div>
              <div className="text-slate-500 flex items-center gap-1 mt-0.5">
                <User size={10} weight="duotone" />
                <span className="truncate max-w-[120px]">{r.gate_in_by_name || r.gate_in_by || "—"}</span>
              </div>
            </div>
          )},
          { key: "gate_out_at", label: "Keluar", render: (r) => (
            r.gate_out_at ? (
              <div className="text-[11px] leading-tight">
                <div className="font-mono text-slate-700">{fmt(r.gate_out_at)}</div>
                <div className="text-slate-500 flex items-center gap-1 mt-0.5">
                  <User size={10} weight="duotone" />
                  <span className="truncate max-w-[120px]">{r.gate_out_by_name || r.gate_out_by || "—"}</span>
                </div>
              </div>
            ) : <span className="text-slate-400 text-[11px]">Masih di area</span>
          )},
          { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
          { key: "actions", label: "", render: (r) => (
            <button
              onClick={() => openDetail(r)}
              data-testid={`log-detail-${r.vehicle_no}`}
              className="text-sky-700 hover:text-sky-900 p-1.5 hover:bg-sky-50 rounded-md transition-colors"
              title="Lihat detail"
            >
              <Eye size={16} weight="duotone" />
            </button>
          )},
        ]}
      />

      <DetailDialog log={detail} onClose={() => setDetail(null)} />
    </AppShell>
  );
}

function DetailDialog({ log, onClose }) {
  if (!log) return null;
  const fmt = (iso) => iso ? new Date(iso).toLocaleString("id-ID", { dateStyle: "full", timeStyle: "medium" }) : "—";

  return (
    <Dialog open={!!log} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg flex items-center gap-3">
            <span className="font-mono">{log.vehicle_no}</span>
            <StatusBadge status={log.status} />
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          {/* Info block */}
          <div className="grid grid-cols-2 gap-4 rounded-lg bg-slate-50 border border-slate-100 p-4 text-[13px]">
            <Info label="Jenis Akses" value={ACCESS_LABEL[log.access_type] || log.access_type} />
            <Info label="Perusahaan" value={log.company} />
            <Info label="Driver" value={log.driver_name} />
            <Info label="Tujuan" value={log.purpose} />
            {log.reference_no && <Info label="No. Referensi" value={log.reference_no} mono />}
          </div>

          {/* Timeline: Masuk */}
          <TimelineRow
            color="sky"
            title="Gerbang Masuk"
            time={fmt(log.gate_in_at)}
            petugas={log.gate_in_by_name || log.gate_in_by}
            email={log.gate_in_by}
            notes={log.notes}
            photo={log.photo_in}
          />

          {/* Timeline: Keluar */}
          {log.gate_out_at ? (
            <TimelineRow
              color="slate"
              title="Gerbang Keluar"
              time={fmt(log.gate_out_at)}
              petugas={log.gate_out_by_name || log.gate_out_by}
              email={log.gate_out_by}
              notes={log.notes_out}
              photo={log.photo_out}
            />
          ) : (
            <div className="rounded-lg border border-dashed border-amber-200 bg-amber-50 p-4 flex items-center gap-3 text-amber-800 text-sm">
              <ClockCounterClockwise size={20} weight="duotone" />
              Kendaraan masih di dalam area. Belum ada proses gerbang keluar.
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button variant="outline" onClick={onClose} data-testid="log-detail-close">Tutup</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Info({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500">{label}</div>
      <div className={`text-slate-800 mt-0.5 ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}

function TimelineRow({ color, title, time, petugas, email, notes, photo }) {
  const dot = color === "sky" ? "bg-sky-500" : "bg-slate-800";
  return (
    <div className="relative pl-6">
      <span className={`absolute left-0 top-2 w-3 h-3 rounded-full ${dot} ring-4 ring-white`} />
      <div className={`absolute left-[5px] top-5 bottom-0 w-px bg-slate-200`} />
      <div className="pb-4">
        <div className="flex items-center gap-2 mb-2">
          <h4 className="font-heading text-[13px] font-bold text-slate-900 uppercase tracking-wider">{title}</h4>
          <span className="text-[10px] font-mono text-slate-500">{time}</span>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2 text-[13px]">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500">Penanggung Jawab</div>
              <div className="flex items-center gap-2 mt-1">
                <div className="w-7 h-7 rounded-full bg-slate-900 text-sky-400 flex items-center justify-center text-xs font-heading font-bold">
                  {(petugas || "?").charAt(0).toUpperCase()}
                </div>
                <div className="leading-tight">
                  <div className="font-semibold text-slate-800">{petugas || "—"}</div>
                  {email && email !== petugas && (
                    <div className="text-[10px] text-slate-500 font-mono">{email}</div>
                  )}
                </div>
              </div>
            </div>
            {notes && (
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500">Catatan</div>
                <div className="text-slate-700 mt-0.5">{notes}</div>
              </div>
            )}
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500 mb-1">Bukti Foto</div>
            {photo ? (
              <div className="rounded-md overflow-hidden border border-slate-200 bg-slate-50 aspect-video">
                <AuthenticatedImage src={photo} alt={title} className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className="rounded-md border border-dashed border-red-200 bg-red-50 aspect-video flex items-center justify-center text-red-600 gap-2 text-xs">
                <ImageIcon size={16} weight="duotone" /> Foto belum ada
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
